import { useLingui } from '@lingui/react/macro'
import { sentinelWindowRange } from 'features/timeSeries/hooks/useSentinelScores'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import type { BaseSensorEntry } from 'types'

import { BAND_SIGMAS, setAgentChartOverlay } from './agentChartOverlay'
import {
  agentKpiChartIssue,
  agentKpiDerivedValue,
  agentKpiPenId,
  expressionTags,
} from './agentKpiPen'
import {
  type ActiveModel,
  claimDraw,
  drawIdentity,
  EMPTY_CARD,
  getKeepOtherPens,
  modelCardState,
  onClearRequested,
  setModelCardState,
  subscribeModelCard,
} from './modelCard'
import {
  expectedPenId,
  modelSeriesDerivedValues,
  residualPenId,
  setModelSeries,
} from './modelSeries'
import { kpiOnlyVisibility } from './penVisibility'
import type { ModelSeries } from './useAgentModels'

/**
 * The chart side of the model card: putting a model on the chart, taking it
 * off, and the pen bookkeeping that makes both reversible. The card itself
 * lives in `modelCard`; this is the part that needs chart context, which is why
 * it is a hook and not more module state.
 *
 * A model the agent is still assembling draws itself. There used to be a "View
 * on chart" button, which was a button asking you to confirm the thing you had
 * just asked the agent to do — and it meant a build you were watching sat
 * invisible until you clicked.
 */
export const useActiveModel = (
  siteId: string | null,
  fetchSeries: (entryId: string) => Promise<ModelSeries | null>
) => {
  const { t } = useLingui()

  const {
    sensorEntries,
    selectedSensors,
    setSelectedSensors,
    setDerivedValues,
    setSensorVisibilityConfig,
    setTimeRange,
    exitLiveTail,
  } = useScatterplotContext()
  const card = useSyncExternalStore(
    subscribeModelCard,
    modelCardState,
    modelCardState
  )
  /** Bumped per publish/clear; a publish that awaited past its own generation
   *  must not write a retired model's card over the current one. */
  const generation = useRef(0)

  const clear = useCallback(() => {
    generation.current += 1
    setAgentChartOverlay(null)
    const { penSnapshot, hiddenPenIds, ownedPenIds } = modelCardState()
    setModelSeries(null, null)
    if (hiddenPenIds.length) {
      setSensorVisibilityConfig((prev) =>
        kpiOnlyVisibility(hiddenPenIds, false, prev)
      )
    }
    if (ownedPenIds.length) {
      setDerivedValues((prev) =>
        prev.filter((entry) => !ownedPenIds.includes(entry.id))
      )
    }
    if (penSnapshot) setSelectedSensors(penSnapshot)
    setModelCardState(EMPTY_CARD)
  }, [setDerivedValues, setSelectedSensors, setSensorVisibilityConfig])

  const publish = useCallback(
    async (model: ActiveModel) => {
      if (!siteId) return
      const gen = ++generation.current
      const state = modelCardState()
      // Snapshot only on the FIRST takeover: activating a second model must not
      // remember the first model's pens as "the user's". Persisted before any
      // of this publish's writes, so a second publish that starts while this
      // one is mid-fetch finds the user's pens rather than reading this
      // publish's half-applied selection as theirs.
      const penSnapshot = state.penSnapshot ?? selectedSensors
      if (!state.penSnapshot) setModelCardState({ ...state, penSnapshot })
      const previouslyOwned = state.ownedPenIds
      const showResidual = state.showResidual

      const derived = agentKpiDerivedValue(model, siteId)
      const penId = agentKpiPenId(model)

      // The chart shows exactly this model's inputs — replacing, never adding.
      // The expression's own tags come FIRST and must all be plotted, or the
      // derived KPI pen (the "actual" line) is dropped by the chart.
      const exprTags = expressionTags(model.expr ?? '', model.roles ?? {})
      const roleTags = Object.values(model.roles ?? {}).filter(Boolean)
      const ordered = [
        ...exprTags,
        ...roleTags.filter((tag) => !exprTags.includes(tag)),
      ]
      const pens = ordered
        .map((id) => sensorEntries.find((sensor) => sensor.id === id))
        .filter((entry): entry is BaseSensorEntry => entry !== undefined)
      const missingForKpi = exprTags.filter(
        (id) => !pens.some((pen) => pen.id === id)
      )
      const kpiOnly = state.kpiOnly

      // Either the model owns the chart outright, or it joins what is already
      // there and the rest is hidden. Hidden, not deselected: a deselected pen
      // has to be found again in the tag browser, a hidden one is a click away
      // in Settings.
      const modelPenIds = new Set(pens.map((pen) => pen.id))
      const others = getKeepOtherPens()
        ? penSnapshot.filter((pen) => !modelPenIds.has(pen.id))
        : []
      setSelectedSensors([...pens, ...others])

      // Carry the KPI-only choice across models: un-hide the previous model's
      // pens, hide this one's. The kept-but-unused pens are hidden either way.
      const hiddenPenIds = kpiOnly ? pens.map((pen) => pen.id) : []
      const otherIds = others.map((pen) => pen.id)
      setSensorVisibilityConfig((prev) => {
        const restored = kpiOnlyVisibility(state.hiddenPenIds, false, prev)
        const withModel = kpiOnlyVisibility(hiddenPenIds, true, restored)
        return kpiOnlyVisibility(otherIds, true, withModel)
      })

      const series =
        model.series ??
        (model.entry_id && !model.unsaved
          ? await fetchSeries(model.entry_id)
          : null)
      if (gen !== generation.current) return

      // The saved fit becomes chart columns: expected always, residual on
      // request. Deterministic — no agent, no re-pull, no re-fit.
      setModelSeries(series ? model.entry_id : null, series ?? null)
      const exprTagIds = derived?.data.dependencyIds ?? []
      const savedOutputPens = series
        ? modelSeriesDerivedValues(
            model.entry_id,
            model.kpi ?? 'KPI',
            exprTagIds,
            siteId,
            { expected: true, residual: showResidual }
          )
        : []
      const ownedPenIds = [
        ...(derived ? [penId] : []),
        ...savedOutputPens.map((entry) => entry.id),
      ]
      setDerivedValues((prev) => [
        ...prev.filter(
          (entry) =>
            !previouslyOwned.includes(entry.id) &&
            !ownedPenIds.includes(entry.id)
        ),
        ...(derived ? [derived] : []),
        ...savedOutputPens,
      ])

      setModelCardState({
        ...modelCardState(),
        model: {
          ...model,
          series,
          siteId,
          missingForKpi,
          // Reported rather than swallowed: a KPI the chart cannot evaluate
          // draws as nothing, and "nothing" is indistinguishable from a model
          // that found no signal.
          kpiIssue: agentKpiChartIssue(model),
        },
        source: 'saved',
        drawn: drawIdentity(model),
        penSnapshot,
        derivedPenId: derived ? penId : null,
        modelPens: pens,
        hiddenPenIds: [...hiddenPenIds, ...otherIds],
        kpiOnly,
        showResidual,
        ownedPenIds,
      })

      const modelName = model.name ?? model.kpi ?? t`agent model`
      const bandSigmas = model.monitor?.level_sigma ?? BAND_SIGMAS
      setAgentChartOverlay({
        target: derived ? penId : (roleTags[0] ?? null),
        baselineStart: model.baseline_window?.[0],
        baselineEnd: model.baseline_window?.[1],
        name: model.name ?? model.kpi ?? 'agent model',
        // The expected value is a pen whenever the saved series is on the chart,
        // so the band must not also stroke it or name it in the readout.
        centerPenId: series ? expectedPenId(model.entry_id) : null,
        bandSigmas,
        health: series?.model_health ?? model.model_health ?? null,
        label: t`${modelName} · expected ±${bandSigmas}σ`,
        series,
      })

      const range = sentinelWindowRange(
        series?.dates?.[0] ?? model.baseline_window?.[0],
        series?.dates?.[series.dates.length - 1] ?? model.data_through,
        Math.max(pens.length, 1)
      )
      if (range) {
        exitLiveTail()
        setTimeRange(range)
      }
    },
    [
      exitLiveTail,
      fetchSeries,
      selectedSensors,
      sensorEntries,
      setDerivedValues,
      setSelectedSensors,
      setSensorVisibilityConfig,
      setTimeRange,
      siteId,
      t,
    ]
  )

  /**
   * Add or remove the residual pen, computed from the SAVED series — the thing
   * that previously required asking the agent to re-fit the model.
   */
  const setShowResidual = useCallback(
    (showResidual: boolean) => {
      const state = modelCardState()
      const model = state.model
      if (!model?.series) return
      const id = residualPenId(model.entry_id)
      if (showResidual) {
        const [entry] = modelSeriesDerivedValues(
          model.entry_id,
          model.kpi ?? 'KPI',
          model.roles ? Object.values(model.roles) : [],
          model.siteId ?? '',
          { expected: false, residual: true }
        )
        // Depend on the same tags the KPI does, so it renders whenever it does.
        const kpiEntry = agentKpiDerivedValue(model, model.siteId ?? '')
        const withDeps = {
          ...entry,
          data: {
            ...entry.data,
            dependencyIds:
              kpiEntry?.data.dependencyIds ?? entry.data.dependencyIds,
          },
        }
        setDerivedValues((prev) => [
          ...prev.filter((existing) => existing.id !== id),
          withDeps,
        ])
        setModelCardState({
          ...state,
          showResidual: true,
          ownedPenIds: [...new Set([...state.ownedPenIds, id])],
        })
      } else {
        setDerivedValues((prev) =>
          prev.filter((existing) => existing.id !== id)
        )
        setModelCardState({
          ...state,
          showResidual: false,
          ownedPenIds: state.ownedPenIds.filter((owned) => owned !== id),
        })
      }
    },
    [setDerivedValues]
  )

  /** Hide (or restore) every tag pen, leaving the KPI and its expectation. */
  const setKpiOnly = useCallback(
    (kpiOnly: boolean) => {
      const state = modelCardState()
      if (!state.model) return
      const ids = state.modelPens.map((pen) => pen.id)
      setSensorVisibilityConfig((prev) =>
        kpiOnlyVisibility(kpiOnly ? ids : state.hiddenPenIds, kpiOnly, prev)
      )
      setModelCardState({
        ...modelCardState(),
        kpiOnly,
        hiddenPenIds: kpiOnly ? ids : [],
      })
    },
    [setSensorVisibilityConfig]
  )

  // clearModelCard needs the chart teardown, which only exists inside the hook.
  useEffect(() => onClearRequested(clear), [clear])

  // A model the agent just folded is not on the chart yet. Drawing is keyed on
  // the fit's own identity so a re-render cannot republish and stomp the pens.
  useEffect(() => {
    const model = card.model
    if (!model?.series?.dates?.length) return
    if (!claimDraw(drawIdentity(model))) return
    void publish(model)
  }, [card.model, publish])

  // A view belongs to its site; switching sites retires it (the pens and the
  // derived KPI would not resolve there anyway). Deliberately NOT on unmount.
  useEffect(() => {
    if (
      card.model &&
      siteId &&
      card.model.siteId &&
      card.model.siteId !== siteId
    ) {
      clear()
    }
  }, [card.model, clear, siteId])

  return {
    active: card.model,
    publish,
    clear,
    kpiOnly: card.kpiOnly,
    setKpiOnly,
    showResidual: card.showResidual,
    setShowResidual,
    /** A residual can only be drawn when the model carries its saved series. */
    canShowResidual: !!card.model?.series,
    /** Tag pens the toggle hides — all of them, KPI inputs included. */
    hidablePenCount: card.modelPens.length,
  }
}
